import axios from 'axios';
import { API_BASE } from './config';

export type CurrentUser = { id: number; email: string; admin: boolean };
export type AuthResponse = { token: string; user: CurrentUser };
export type ChangePasswordResponse = { ok: boolean; token: string };

const TOKEN_STORAGE_KEY = 'piCustodianAuthToken';

const ensureCurrentUser = (data: unknown): CurrentUser => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid auth payload');
  }
  const user = data as Partial<CurrentUser>;
  if (typeof user.id !== 'number' || typeof user.email !== 'string' || typeof user.admin !== 'boolean') {
    throw new Error('Invalid auth payload');
  }
  return user as CurrentUser;
};

const setAuthHeader = (token: string | null) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const getStoredToken = () => localStorage.getItem(TOKEN_STORAGE_KEY);

export const storeToken = (token: string) => {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  setAuthHeader(token);
};

export const clearStoredToken = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  setAuthHeader(null);
};

export const initAuthToken = () => {
  const token = getStoredToken();
  if (token) {
    setAuthHeader(token);
  }
  return token;
};

export async function signIn(email: string, password: string): Promise<CurrentUser> {
  const res = await axios.post<AuthResponse>(`${API_BASE}/users/sign_in`, {
    user: { email, password },
  });
  const user = ensureCurrentUser(res.data.user);
  storeToken(res.data.token);
  return user;
}

export async function signOut(): Promise<void> {
  try {
    await axios.delete(`${API_BASE}/users/sign_out`);
  } finally {
    clearStoredToken();
  }
}

export async function fetchCurrentUser(): Promise<CurrentUser> {
  const res = await axios.get<CurrentUser>(`${API_BASE}/me`);
  return ensureCurrentUser(res.data);
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
  newPasswordConfirmation: string
): Promise<void> {
  const res = await axios.patch<ChangePasswordResponse>(`${API_BASE}/me/password`, {
    current_password: currentPassword,
    new_password: newPassword,
    new_password_confirmation: newPasswordConfirmation,
  });
  if (typeof res.data?.token !== 'string') {
    throw new Error('Invalid auth payload');
  }
  storeToken(res.data.token);
}
